import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Badge,
  Box,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
  Drawer,
  Divider,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Chip,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Switch
} from '@mui/material';
import TextField from '@mui/material/TextField';

// project-imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Avatar from 'components/@extended/Avatar';
import { APPSYNC_EVENTS_WS_DEBUG, getSubFromIdToken, subscribeUserNotificationsWS } from 'utils/appsyncEventsWs';
import useAuth from 'hooks/useAuth';

// action-manager
import { APIAction } from 'utils/action-manager/api/apiManager';
import handleAction from 'utils/action-manager/actionManager';

// assets
import {
  Add,
  Notification as NotificationIcon,
  MessageText1,
  Setting2,
  Buildings,
  Home,
  Calendar,
  DiscountShape,
  TableDocument
} from 'iconsax-react';

// types
import { ThemeMode } from 'types/config';
import { FormattedMessage, useIntl } from 'react-intl';

type NotificationItem = {
  notificationid: number;
  type?: string | null;
  tag?: string | null;
  messagekey?: string | null;
  title_resolved?: string | null;
  description_resolved?: string | null;
  imageurl?: string | null;
  videourl?: string | null;
  payloadjson?: any;
  varsjson?: any;
  createdat?: string | null;
  createdat_iso?: string | null;
  seenat?: string | null;
  seenat_iso?: string | null;
  readat?: string | null;
  readat_iso?: string | null;
  // si lo devuelves
  app?: string | null;
};

type NotificationCategoryFilter = 'all' | 'development' | 'offer' | 'contactForm' | 'customerRegistration';
type NotificationSettings = {
  emailEnabled: boolean;
  email: string;
  useAccountEmail: boolean;
};

const SHOW_MOCK_UNREAD_PREVIEW = APPSYNC_EVENTS_WS_DEBUG;

function formatRelativeDate(iso: string | null | undefined, intl: any) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return intl.formatMessage({ id: 'notifications.time.justNow', defaultMessage: 'just now' });
  if (mins < 60) {
    return intl.formatMessage({ id: 'notifications.time.minutesAgo', defaultMessage: '{count} min ago' }, { count: mins });
  }
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) {
    return intl.formatMessage({ id: 'notifications.time.hoursAgo', defaultMessage: '{count} hours ago' }, { count: hrs });
  }
  return d.toLocaleDateString(intl.locale);
}

function resolveNotificationTexts(
  notification: NotificationItem,
  intl: any
): {
  title: string;
  description: string;
  offerName?: string;
  developmentName?: string;
  stageName?: string;
  fullname?: string;
  eventTitle?: string;
  offerTemplate?: 'view' | 'developmentView';
  contactTemplate?: 'received';
  developmentTemplate?: 'sold' | 'newStage' | 'created';
  developmentEventTemplate?: 'showFlat' | 'openHouse';
} {
  const row = notification as any;
  const fallbackTitle = notification.title_resolved || notification.type || 'Notification';
  const fallbackDescription = notification.description_resolved || '';

  const messageKey = String(row.messagekey || row.messageKey || row.MESSAGEKEY || row.message_key || '').trim();
  const normalizedMessageKey = messageKey.toLowerCase();

  const varsRaw = row.varsjson ?? row.varsJson ?? row.VARSJSON;
  const vars =
    varsRaw && typeof varsRaw === 'object'
      ? varsRaw
      : typeof varsRaw === 'string'
      ? (() => {
          try {
            return JSON.parse(varsRaw);
          } catch {
            return {};
          }
        })()
      : {};
  const payloadRaw = row.payloadjson ?? row.payloadJson ?? row.PAYLOADJSON;
  const payload =
    payloadRaw && typeof payloadRaw === 'object'
      ? payloadRaw
      : typeof payloadRaw === 'string'
      ? (() => {
          try {
            return JSON.parse(payloadRaw);
          } catch {
            return {};
          }
        })()
      : {};

  const offerName = String(vars.offerName ?? vars.offername ?? vars.offer_name ?? '—');
  const developmentName = String(
    vars.developmentName ?? vars.developmentname ?? vars.development_name ?? payload.developmentName ?? payload.development_name ?? '—'
  );
  const stageName = String(vars.stageName ?? vars.stagename ?? vars.stage_name ?? payload.stageName ?? payload.stage_name ?? '—');
  const fullname = String(vars.fullname ?? vars.fullName ?? payload.fullname ?? payload.fullName ?? '—');
  const eventTitle = String(vars.eventTitle ?? vars.eventtitle ?? vars.event_title ?? payload.eventTitle ?? payload.eventtitle ?? '—');
  const eventType = String(vars.type ?? payload.type ?? '').toLowerCase();
  const hasOfferContext = !!vars.offerName || !!vars.offername || !!vars.offer_name;
  const hasDevelopmentContext = !!vars.developmentName || !!vars.developmentname || !!vars.development_name;
  const type = String(notification.type || '').toLowerCase();

  if (normalizedMessageKey === 'notifications.offer.view' || normalizedMessageKey === 'offer.view') {
    return {
      title: intl.formatMessage({ id: 'notifications.offer.title', defaultMessage: 'Offer notification' }),
      description: intl.formatMessage(
        { id: 'notifications.offer.view.message', defaultMessage: 'The offer {offerName} has received a new view.' },
        { offerName }
      ),
      offerName,
      offerTemplate: 'view'
    };
  }

  if (normalizedMessageKey === 'notifications.offer.developmentview' || normalizedMessageKey === 'offer.promo_view') {
    return {
      title: intl.formatMessage({ id: 'notifications.offer.title', defaultMessage: 'Offer notification' }),
      description: intl.formatMessage(
        {
          id: 'notifications.offer.developmentView.message',
          defaultMessage: 'The development {developmentName} in the offer {offerName} has received a new view.'
        },
        { developmentName, offerName }
      ),
      offerName,
      developmentName,
      offerTemplate: 'developmentView'
    };
  }

  if (normalizedMessageKey === 'notifications.contactform.received') {
    return {
      title: intl.formatMessage({ id: 'notifications.contactForm.title', defaultMessage: 'New contact form' }),
      description: intl.formatMessage(
        { id: 'notifications.contactForm.received.message', defaultMessage: '{fullname} has sent a contact form.' },
        { fullname }
      ),
      fullname,
      contactTemplate: 'received'
    };
  }

  if (normalizedMessageKey === 'notifications.development.sold' || normalizedMessageKey === 'development.sold') {
    return {
      title: intl.formatMessage({ id: 'notifications.development.title', defaultMessage: 'Development notification' }),
      description: intl.formatMessage(
        {
          id: 'notifications.development.sold.message',
          defaultMessage: 'The development {developmentName} has been fully sold.'
        },
        { developmentName }
      ),
      developmentName,
      developmentTemplate: 'sold'
    };
  }

  if (
    normalizedMessageKey === 'notifications.development.newstage' ||
    normalizedMessageKey === 'development.newstage' ||
    normalizedMessageKey === 'notifications.development.new_stage' ||
    normalizedMessageKey === 'development.new_stage'
  ) {
    return {
      title: intl.formatMessage({ id: 'notifications.development.title', defaultMessage: 'Development notification' }),
      description: intl.formatMessage(
        {
          id: 'notifications.development.newStage.message',
          defaultMessage: 'A new stage ({stageName}) has been added to development {developmentName}.'
        },
        { stageName, developmentName }
      ),
      stageName,
      developmentName,
      developmentTemplate: 'newStage'
    };
  }

  if (normalizedMessageKey === 'notifications.development.created' || normalizedMessageKey === 'development.created') {
    return {
      title: intl.formatMessage({ id: 'notifications.development.title', defaultMessage: 'Development notification' }),
      description: intl.formatMessage(
        {
          id: 'notifications.development.created.message',
          defaultMessage: 'The development {developmentName} has been added.'
        },
        { developmentName }
      ),
      developmentName,
      developmentTemplate: 'created'
    };
  }

  if (normalizedMessageKey === 'notifications.developmentevent.showflat.created' || eventType === 'show-flat') {
    return {
      title: intl.formatMessage({ id: 'notifications.developmentEvent.title', defaultMessage: 'Development notification' }),
      description: intl.formatMessage(
        {
          id: 'notifications.developmentEvent.showFlat.message',
          defaultMessage: 'New show flat event ({eventTitle}) in development {developmentName}.'
        },
        { eventTitle, developmentName }
      ),
      eventTitle,
      developmentName,
      developmentEventTemplate: 'showFlat'
    };
  }

  if (normalizedMessageKey === 'notifications.developmentevent.openhouse.created' || eventType === 'open-house') {
    return {
      title: intl.formatMessage({ id: 'notifications.developmentEvent.title', defaultMessage: 'Development notification' }),
      description: intl.formatMessage(
        {
          id: 'notifications.developmentEvent.openHouse.message',
          defaultMessage: 'New open house event ({eventTitle}) in development {developmentName}.'
        },
        { eventTitle, developmentName }
      ),
      eventTitle,
      developmentName,
      developmentEventTemplate: 'openHouse'
    };
  }

  // Fallback robusto para payloads de oferta sin messagekey normalizado.
  if (type.includes('offer') && hasOfferContext) {
    if (hasDevelopmentContext) {
      return {
        title: intl.formatMessage({ id: 'notifications.offer.title', defaultMessage: 'Offer notification' }),
        description: intl.formatMessage(
          {
            id: 'notifications.offer.developmentView.message',
            defaultMessage: 'The development {developmentName} in the offer {offerName} has received a new view.'
          },
          { developmentName, offerName }
        ),
        offerName,
        developmentName,
        offerTemplate: 'developmentView'
      };
    }
    return {
      title: intl.formatMessage({ id: 'notifications.offer.title', defaultMessage: 'Offer notification' }),
      description: intl.formatMessage(
        { id: 'notifications.offer.view.message', defaultMessage: 'The offer {offerName} has received a new view.' },
        { offerName }
      ),
      offerName,
      offerTemplate: 'view'
    };
  }

  return { title: fallbackTitle, description: fallbackDescription };
}

function pickIcon(type?: string | null) {
  const t = (type || '').toLowerCase();
  if (t.includes('development')) return <Buildings size={20} variant="Bold" />;
  if (t.includes('show-flat')) return <Home size={20} variant="Bold" />;
  if (t.includes('open-house')) return <Calendar size={20} variant="Bold" />;
  if (t.includes('offer')) return <DiscountShape size={20} variant="Bold" />;
  if (t.includes('contact')) return <TableDocument size={20} variant="Bold" />;
  if (t.includes('settings') || t.includes('config')) return <Setting2 size={20} variant="Bold" />;
  return <MessageText1 size={20} variant="Bold" />;
}

function resolveAppFromHost(hostname: string) {
  const h = (hostname || '').toLowerCase();
  if (h.includes('admin.')) return 'admin';
  if (h.includes('agent.')) return 'agent';
  if (h.includes('developer.')) return 'developer';
  if (h.includes('collab.')) return 'collab';
  return 'agent'; // fallback razonable
}

function parseNotificationSettings(raw: string | undefined, accountEmail?: string): NotificationSettings {
  const fallback: NotificationSettings = {
    emailEnabled: false,
    email: accountEmail || '',
    useAccountEmail: true
  };

  if (!raw) return fallback;

  try {
    const parsed = JSON.parse(raw);
    const useAccountEmail = parsed?.useAccountEmail !== false;
    return {
      emailEnabled: parsed?.emailEnabled === true,
      email: useAccountEmail ? accountEmail || '' : String(parsed?.email || ''),
      useAccountEmail
    };
  } catch {
    return fallback;
  }
}

function matchesNotificationFilter(item: NotificationItem, filter: NotificationCategoryFilter): boolean {
  if (filter === 'all') return true;

  const type = String(item.type || '').toLowerCase();
  // const tag = String(item.tag || '').toLowerCase();
  // const messageKey = String((item as any).messagekey || '').toLowerCase();
  // const combined = `${type} ${tag} ${messageKey}`;
  const combined = type;

  if (filter === 'development') {
    return (
      combined.includes('development') || combined.includes('promo') || combined.includes('open-house') || combined.includes('show-flat')
    );
  }
  if (filter === 'offer') return combined.includes('offer');
  if (filter === 'contactForm') return combined.includes('contact');
  if (filter === 'customerRegistration') return combined.includes('customer') || combined.includes('registration');

  return true;
}

const NotificationPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const intl = useIntl();
  const { isLoggedIn, isInitialized, user, updateProfile } = useAuth();
  const anchorRef = useRef<any>(null);
  const openRef = useRef(false);
  const listScrollRef = useRef<HTMLDivElement | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [wsRetryTick, setWsRetryTick] = useState(0);

  const app = useMemo(() => resolveAppFromHost(window.location.hostname), []);

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'unread' | 'read'>('unread');
  const [filter, setFilter] = useState<NotificationCategoryFilter>('all');
  const [animateItems, setAnimateItems] = useState(false);
  const animateItemsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showList, setShowList] = useState(true);
  const listSwitchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [unreadCount, setUnreadCount] = useState<number>(0);

  const [itemsUnread, setItemsUnread] = useState<NotificationItem[]>([]);
  const [itemsRead, setItemsRead] = useState<NotificationItem[]>([]);
  const [cursorUnread, setCursorUnread] = useState<number | null>(null);
  const [cursorRead, setCursorRead] = useState<number | null>(null);
  const [hasMoreUnread, setHasMoreUnread] = useState(false);
  const [hasMoreRead, setHasMoreRead] = useState(false);

  const [loadingUnread, setLoadingUnread] = useState(false);
  const [loadingRead, setLoadingRead] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>(() =>
    parseNotificationSettings(user?.notifSettings, user?.email)
  );

  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'secondary.200' : 'secondary.200';
  const iconBackColor = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'secondary.100';

  const lang = useMemo(() => (intl.locale?.startsWith('es') ? 'es' : intl.locale), [intl.locale]);

  const handleToggle = () => setOpen((prev) => !prev);

  useEffect(() => {
    setNotificationSettings(parseNotificationSettings(user?.notifSettings, user?.email));
  }, [user?.notifSettings, user?.email]);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    return () => {
      if (animateItemsTimerRef.current) {
        clearTimeout(animateItemsTimerRef.current);
        animateItemsTimerRef.current = null;
      }
      if (listSwitchTimerRef.current) {
        clearTimeout(listSwitchTimerRef.current);
        listSwitchTimerRef.current = null;
      }
    };
  }, []);

  const armItemAnimations = () => {
    setAnimateItems(true);
    if (animateItemsTimerRef.current) clearTimeout(animateItemsTimerRef.current);
    animateItemsTimerRef.current = setTimeout(() => {
      setAnimateItems(false);
    }, 900);
  };

  const switchListWithFade = (next: () => void) => {
    setShowList(false);
    next();
    if (listSwitchTimerRef.current) clearTimeout(listSwitchTimerRef.current);
    listSwitchTimerRef.current = setTimeout(() => {
      setShowList(true);
    }, 60);
  };

  const api: any = async (method: string, data?: any) => {
    const action: APIAction = { type: 'api', entity: 'notifications', method, data };
    return handleAction(action);
  };

  const refreshUnreadCount = async () => {
    try {
      const res = await api('unread-count', { app });
      setUnreadCount(res?.unread || 0);
    } catch (e) {
      console.error(e);
    }
  };

  const loadList = async (status: 'unread' | 'read', mode: 'reset' | 'more' = 'reset') => {
    const isUnread = status === 'unread';
    const setLoading = isUnread ? setLoadingUnread : setLoadingRead;
    const cursor = isUnread ? cursorUnread : cursorRead;
    const setCursor = isUnread ? setCursorUnread : setCursorRead;
    const setItems = isUnread ? setItemsUnread : setItemsRead;
    const setHasMore = isUnread ? setHasMoreUnread : setHasMoreRead;

    setLoading(true);
    try {
      const res = await api('list', {
        status,
        limit: 20,
        cursor: mode === 'more' ? cursor : null,
        lang,
        app
      });

      const newItems: NotificationItem[] = res?.items || [];
      const nextCursor: number | null = res?.nextCursor ?? null;

      if (mode === 'reset') setItems(newItems);
      else setItems((prev) => [...(prev || []), ...newItems]);

      setCursor(nextCursor);
      setHasMore(nextCursor !== null);

      if (status === 'unread') {
        // Best-effort: marcar SEEN en los que aún no estén vistos
        const toSeen = newItems.filter((n) => !n.seenat).map((n) => n.notificationid);
        await Promise.allSettled(toSeen.map((id) => api('seen', { id, app })));
        refreshUnreadCount();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const scheduleRefreshUnreadCount = () => {
    if (refreshTimerRef.current) return;
    refreshTimerRef.current = setTimeout(async () => {
      refreshTimerRef.current = null;
      await refreshUnreadCount();
    }, 250);
  };

  const scheduleReloadUnreadList = () => {
    if (reloadTimerRef.current) return;
    reloadTimerRef.current = setTimeout(async () => {
      reloadTimerRef.current = null;
      await loadList('unread', 'reset');
    }, 500);
  };

  const markReadAndMove = async (n: NotificationItem) => {
    try {
      armItemAnimations();
      await api('read', { id: n.notificationid, app });
      setItemsUnread((prev) => prev.filter((x) => x.notificationid !== n.notificationid));
      setItemsRead((prev) => [{ ...n, readat_iso: new Date().toISOString() }, ...prev]);
      refreshUnreadCount();
    } catch (e) {
      console.error(e);
    }
  };

  const markAllRead = async () => {
    setMarkingAllRead(true);
    try {
      await api('read-all', { app });

      const nowIso = new Date().toISOString();
      const movedUnread = itemsUnread
        .filter((item) => item.notificationid >= 0)
        .map((item) => ({ ...item, readat_iso: item.readat_iso || nowIso, readat: item.readat || nowIso }));
      const mockUnread = itemsUnread.filter((item) => item.notificationid < 0);

      setItemsUnread(mockUnread);
      setItemsRead((prev) => [...movedUnread, ...prev]);
      setUnreadCount(0);
      setCursorUnread(null);
      setHasMoreUnread(false);
      await loadList('read', 'reset');
    } catch (e) {
      console.error(e);
    } finally {
      setMarkingAllRead(false);
    }
  };

  const saveNotificationSettings = async () => {
    setSettingsSaving(true);
    try {
      const payload: NotificationSettings = {
        emailEnabled: notificationSettings.emailEnabled,
        useAccountEmail: notificationSettings.useAccountEmail,
        email: notificationSettings.useAccountEmail ? user?.email || '' : notificationSettings.email.trim()
      };

      await updateProfile({ 'custom:notif_settings': JSON.stringify(payload) });
      setSettingsOpen(false);
    } catch (error) {
      console.error(error);
    } finally {
      setSettingsSaving(false);
    }
  };

  const resolveNotificationTarget = (n: NotificationItem): { route?: string; url?: string } => {
    const payload = (n.payloadjson || {}) as any;
    const t = String(n.type || '').toLowerCase();
    const kind = String(payload.kind || '').toLowerCase();

    // Contact forms always redirect to internal list route.
    if (t.includes('contact') || kind === 'contact-form') return { route: '/contact-forms' };

    if (typeof payload.route === 'string' && payload.route.trim()) {
      return { route: payload.route.trim() };
    }
    if (typeof payload.url === 'string' && payload.url.trim()) {
      return { url: payload.url.trim() };
    }

    const developmentId = payload.developmentId || payload.promotionId || payload.promoId || payload.development_id || payload.promotion_id;
    if (developmentId && (t.includes('development') || t.includes('open-house') || t.includes('show-flat'))) {
      return { route: `/developments/${developmentId}` };
    }

    if (t.includes('offer')) return { route: '/tools/offers' };

    return {};
  };

  const openNotificationTarget = async (n: NotificationItem) => {
    const target = resolveNotificationTarget(n);
    if (!target.route && !target.url) return;

    const isUnreadRow = !n.readat && !n.readat_iso;
    const isMockRow = n.notificationid < 0;

    if (isUnreadRow && !isMockRow) {
      await markReadAndMove(n);
    }

    setOpen(false);

    if (target.url) {
      window.open(target.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (target.route) navigate(target.route);
  };

  useEffect(() => {
    let cleanup: null | (() => void) = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const namespace = process.env.REACT_APP_APPSYNC_EVENTS_NAMESPACE || 'notifications';

    const idToken = localStorage.getItem('idToken') || '';
    if (!idToken) {
      // If auth is ready but token is not yet persisted, retry once shortly.
      if (isInitialized && isLoggedIn) {
        retryTimer = setTimeout(() => setWsRetryTick((v) => v + 1), 1200);
      }
      return () => {
        if (retryTimer) clearTimeout(retryTimer);
      };
    }

    const sub = getSubFromIdToken(idToken);

    if (!sub) {
      console.error('[AppSync Events] Cannot decode sub from idToken');
      return;
    }

    const channelPath = `/${namespace}/user/${sub}`;

    try {
      cleanup = subscribeUserNotificationsWS({
        channelPath,
        debug: APPSYNC_EVENTS_WS_DEBUG,
        onConnected: () => {
          if (APPSYNC_EVENTS_WS_DEBUG) console.log('[AppSync Events] connected');
        },
        onEvent: async (evt) => {
          if (APPSYNC_EVENTS_WS_DEBUG) console.log('[AppSync Events] event', evt);
          armItemAnimations();
          scheduleRefreshUnreadCount();

          if (openRef.current) {
            scheduleReloadUnreadList();
          }
        },
        onError: (err) => {
          if (APPSYNC_EVENTS_WS_DEBUG) console.error('[AppSync Events] error', err);
        }
      });
    } catch (err) {
      if (APPSYNC_EVENTS_WS_DEBUG) console.error('[AppSync Events] subscribe init failed', err);
    }

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      cleanup?.();
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      if (reloadTimerRef.current) {
        clearTimeout(reloadTimerRef.current);
        reloadTimerRef.current = null;
      }
    };
    // NO metas lang aquí o re-suscribes por idioma
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app, isInitialized, isLoggedIn, wsRetryTick]);

  // ... tu useEffect que carga listas cuando abres drawer:
  useEffect(() => {
    if (!open) return;
    setFilter('all');
    refreshUnreadCount();
    loadList('unread', 'reset');
    loadList('read', 'reset');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lang]);

  useEffect(() => {
    refreshUnreadCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listSx = {
    '& .MuiListItemButton-root': {
      p: 1.5,
      my: 1.5,
      border: `1px solid ${theme.palette.divider}`,
      alignItems: 'flex-start',
      '&:hover': {
        bgcolor: 'primary.lighter',
        borderColor: theme.palette.primary.light
      }
    }
  };

  const currentItems = tab === 'unread' ? itemsUnread : itemsRead;
  const now = Date.now();
  const mockUnreadItems: NotificationItem[] = [
    {
      notificationid: -999001,
      type: 'development',
      title_resolved: 'Mock promo',
      description_resolved: 'Mensaje de prueba para tipo promo.',
      payloadjson: { route: '/developments/46994455' },
      createdat_iso: new Date(now - 2 * 60 * 1000).toISOString(),
      readat_iso: null
    },
    {
      notificationid: -999002,
      type: 'open-house',
      title_resolved: 'Mock open-house',
      description_resolved: 'Mensaje de prueba para tipo open-house.',
      payloadjson: { route: '/developments/46994455' },
      createdat_iso: new Date(now - 8 * 60 * 1000).toISOString(),
      readat_iso: null
    },
    {
      notificationid: -999003,
      type: 'show-flat',
      title_resolved: 'Mock show-flat',
      description_resolved: 'Mensaje de prueba para tipo show-flat.',
      payloadjson: { route: '/developments/46994455' },
      createdat_iso: new Date(now - 20 * 60 * 1000).toISOString(),
      readat_iso: null
    },
    {
      notificationid: -999004,
      type: 'settings',
      title_resolved: 'Mock settings',
      description_resolved: 'Mensaje de prueba para tipo settings.',
      payloadjson: { route: '/account/my-account' },
      createdat_iso: new Date(now - 60 * 60 * 1000).toISOString(),
      readat_iso: null
    },
    {
      notificationid: -999005,
      type: 'config',
      title_resolved: 'Mock config',
      description_resolved: 'Mensaje de prueba para tipo config.',
      payloadjson: { route: '/account/my-account' },
      createdat_iso: new Date(now - 2 * 60 * 60 * 1000).toISOString(),
      readat_iso: null
    },
    {
      notificationid: -999006,
      type: 'offer-views',
      title_resolved: 'Mock ofertas',
      description_resolved: 'Notificación de oferta: redirige a la sección de ofertas.',
      payloadjson: { route: '/tools/offers' },
      createdat_iso: new Date(now - 4 * 60 * 60 * 1000).toISOString(),
      readat_iso: null
    },
    {
      notificationid: -999007,
      type: 'contact-form',
      title_resolved: 'Mock formulario de contacto',
      description_resolved: 'Notificación de formulario de contacto: redirige al listado de formularios.',
      payloadjson: { route: '/contact-forms' },
      createdat_iso: new Date(now - 6 * 60 * 60 * 1000).toISOString(),
      readat_iso: null
    },
    {
      notificationid: -999008,
      type: 'customer-registration',
      title_resolved: 'Mock registro de clientes',
      description_resolved:
        'Sin ruta asociada de momento (no debería mostrar botón de navegación). También sirve para validar fallback de icono y layout con texto largo.',
      createdat_iso: new Date(now - 26 * 60 * 60 * 1000).toISOString(),
      readat_iso: null
    }
  ];
  const itemsToRender = tab === 'unread' && SHOW_MOCK_UNREAD_PREVIEW ? [...mockUnreadItems, ...currentItems] : currentItems;
  const filteredItemsToRender = itemsToRender.filter((n) => matchesNotificationFilter(n, filter));
  const currentLoading = tab === 'unread' ? loadingUnread : loadingRead;
  const currentHasMore = tab === 'unread' ? hasMoreUnread : hasMoreRead;

  // ✅ SOLO PARA TEST (si añades debug-create)
  const createDebug = async () => {
    try {
      await api('debug-create', {
        app,
        type: 'test',
        tag: 'common',
        titleI18n: { es: 'Notificación de prueba', en: 'Test notification' },
        descriptionI18n: { es: 'Hola 👋 desde el sistema', en: 'Hello 👋 from the system' }
      });
      // await refreshUnreadCount();
      // await loadList(tab);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.5 }}>
      <IconButton
        color="secondary"
        variant="light"
        aria-label="open notifications"
        ref={anchorRef}
        aria-controls={open ? 'notification-drawer' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        size="large"
        sx={{ color: 'secondary.main', bgcolor: open ? iconBackColorOpen : iconBackColor, p: 1 }}
      >
        <Badge badgeContent={unreadCount} color="primary" sx={{ '& .MuiBadge-badge': { top: 2, right: 4 } }}>
          <NotificationIcon variant="Bold" />
        </Badge>
      </IconButton>

      <Drawer
        id="notification-drawer"
        sx={{ zIndex: 2001 }}
        anchor="right"
        onClose={handleToggle}
        open={open}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 600 },
            height: { xs: '100dvh', sm: '100%' },
            maxWidth: { xs: '100vw', sm: 600 },
            maxHeight: { xs: '100dvh', sm: '100%' },
            m: 0,
            borderRadius: { xs: 0, sm: 0 }
          }
        }}
      >
        {open && (
          <MainCard content={false} border={false} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" justifyContent="space-between" sx={{ px: 3, py: 3 }}>
              <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                <NotificationIcon variant="Bulk" />
                <Typography variant="h5">
                  <FormattedMessage id="notifications.title" defaultMessage="Notifications" />
                </Typography>
                {APPSYNC_EVENTS_WS_DEBUG && (
                  <Chip label="Test" size="small" variant="outlined" onClick={createDebug} sx={{ ml: 1, cursor: 'pointer' }} />
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  size="small"
                  aria-label="notification settings"
                  color="secondary"
                  onClick={() => setSettingsOpen((prev) => !prev)}
                >
                  <Setting2 size={18} />
                </IconButton>
                <IconButton size="small" aria-label="close" color="error" onClick={handleToggle}>
                  <Add style={{ transform: 'rotate(45deg)' }} />
                </IconButton>
              </Stack>
            </Stack>

            <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
              <Box sx={{ px: 3, pb: 2 }}>
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    bgcolor: theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'grey.50',
                    px: 2,
                    py: 2
                  }}
                >
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {intl.formatMessage({
                        id: 'notifications.settings.title',
                        defaultMessage: 'Configuración de notificaciones'
                      })}
                    </Typography>

                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                      <Stack spacing={0.25} sx={{ pr: 1 }}>
                        <Typography variant="body1" fontWeight={600}>
                          {intl.formatMessage({
                            id: 'notifications.settings.emailEnabled',
                            defaultMessage: 'Activar notificaciones por email'
                          })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {intl.formatMessage({
                            id: 'notifications.settings.emailEnabledHelp',
                            defaultMessage: 'Recibe avisos de notificaciones también por correo.'
                          })}
                        </Typography>
                      </Stack>
                      <Switch
                        size="small"
                        checked={notificationSettings.emailEnabled}
                        onChange={(event) =>
                          setNotificationSettings((prev) => ({
                            ...prev,
                            emailEnabled: event.target.checked
                          }))
                        }
                      />
                    </Stack>

                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary" fontWeight={600}>
                        {intl.formatMessage({
                          id: 'notifications.settings.email',
                          defaultMessage: 'Email para recibir notificaciones'
                        })}
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="email"
                        placeholder={intl.formatMessage({
                          id: 'notifications.settings.emailPlaceholder',
                          defaultMessage: 'nombre@empresa.com'
                        })}
                        value={notificationSettings.useAccountEmail ? user?.email || '' : notificationSettings.email}
                        onChange={(event) =>
                          setNotificationSettings((prev) => ({
                            ...prev,
                            email: event.target.value
                          }))
                        }
                        disabled={notificationSettings.useAccountEmail}
                        sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}
                      />
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                          <Checkbox
                            checked={notificationSettings.useAccountEmail}
                            onChange={(event) =>
                              setNotificationSettings((prev) => ({
                                ...prev,
                                useAccountEmail: event.target.checked,
                                email: event.target.checked ? user?.email || '' : prev.email
                              }))
                            }
                          />
                        }
                        label={
                          <Typography variant="body2" color="text.secondary">
                            {intl.formatMessage({
                              id: 'notifications.settings.useAccountEmail',
                              defaultMessage: 'Mismo que la cuenta'
                            })}
                          </Typography>
                        }
                      />
                    </Stack>

                    <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
                      <Button variant="outlined" color="secondary" onClick={() => setSettingsOpen(false)} sx={{ textTransform: 'none' }}>
                        {intl.formatMessage({ id: 'notifications.settings.close', defaultMessage: 'Cerrar' })}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={saveNotificationSettings}
                        disabled={settingsSaving}
                        sx={{ textTransform: 'none' }}
                      >
                        {settingsSaving
                          ? intl.formatMessage({ id: 'notifications.actions.loading', defaultMessage: 'Loading...' })
                          : intl.formatMessage({ id: 'notifications.settings.save', defaultMessage: 'Guardar' })}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Box>
            </Collapse>

            <Divider />

            <Box>
              <Tabs
                value={tab}
                onChange={(_, v) => {
                  setAnimateItems(false);
                  switchListWithFade(() => {
                    setTab(v);
                    if (listScrollRef.current) listScrollRef.current.scrollTop = 0;
                  });
                }}
                variant="fullWidth"
                sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 }, mb: 0 }}
              >
                <Tab
                  value="unread"
                  label={
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography fontWeight={600}>
                        <FormattedMessage id="notifications.tabs.unread" defaultMessage="New" />
                      </Typography>
                      <Badge badgeContent={unreadCount} color="primary" />
                    </Stack>
                  }
                />
                <Tab value="read" label={<FormattedMessage id="notifications.tabs.read" defaultMessage="Read" />} />
              </Tabs>

              <Divider />

              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ px: 3, py: 1.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {intl.formatMessage({ id: 'notifications.filters.label', defaultMessage: 'Filter by' })}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 220, width: { xs: '70%', sm: 260 } }}>
                  <Select
                    value={filter}
                    onChange={(e) => {
                      setAnimateItems(false);
                      switchListWithFade(() => {
                        setFilter(e.target.value as NotificationCategoryFilter);
                        if (listScrollRef.current) listScrollRef.current.scrollTop = 0;
                      });
                    }}
                  >
                    <MenuItem value="all">{intl.formatMessage({ id: 'notifications.filters.all', defaultMessage: 'All' })}</MenuItem>
                    <MenuItem value="development">
                      {intl.formatMessage({ id: 'notifications.filters.development', defaultMessage: 'Development' })}
                    </MenuItem>
                    <MenuItem value="offer">{intl.formatMessage({ id: 'notifications.filters.offer', defaultMessage: 'Offer' })}</MenuItem>
                    <MenuItem value="contactForm">
                      {intl.formatMessage({ id: 'notifications.filters.contactForms', defaultMessage: 'Contact forms' })}
                    </MenuItem>
                    <MenuItem value="customerRegistration">
                      {intl.formatMessage({ id: 'notifications.filters.customerRegistration', defaultMessage: 'Customer registration' })}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Divider />
            </Box>
            <Box ref={listScrollRef} sx={{ flex: 1, minHeight: 0, px: 3, py: 2, overflowY: 'auto' }}>
              {tab === 'unread' && unreadCount > 0 && (
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="text"
                    disabled={markingAllRead}
                    onClick={markAllRead}
                    sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                  >
                    {markingAllRead
                      ? intl.formatMessage({ id: 'notifications.actions.loading', defaultMessage: 'Loading...' })
                      : intl.formatMessage({ id: 'notifications.actions.markAllRead', defaultMessage: 'Mark all as read' })}
                  </Button>
                </Stack>
              )}

              {currentLoading && filteredItemsToRender.length === 0 ? (
                <Stack alignItems="center" sx={{ py: 6 }}>
                  <CircularProgress size={26} />
                </Stack>
              ) : filteredItemsToRender.length === 0 ? (
                <Stack alignItems="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    {tab === 'unread'
                      ? intl.formatMessage({ id: 'notifications.tabs.noUnread', defaultMessage: 'No new notifications.' })
                      : intl.formatMessage({ id: 'notifications.tabs.noRead', defaultMessage: 'There is no notification yet.' })}
                  </Typography>
                </Stack>
              ) : !showList ? (
                <Box sx={{ minHeight: 120 }} />
              ) : (
                <Box
                  key={`${tab}-${filter}`}
                  component={motion.div}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.12, ease: 'easeOut' }}
                >
                  <List component="nav" sx={listSx}>
                    <AnimatePresence initial={false}>
                      {filteredItemsToRender.map((n) => {
                        const {
                          title,
                          description: desc,
                          offerName,
                          developmentName,
                          stageName,
                          fullname,
                          eventTitle,
                          offerTemplate,
                          contactTemplate,
                          developmentTemplate,
                          developmentEventTemplate
                        } = resolveNotificationTexts(n, intl);
                        const created = (n.createdat_iso || n.createdat) ?? null;
                        const secondary =
                          offerTemplate === 'view' ? (
                            <FormattedMessage
                              id="notifications.offer.view.message"
                              defaultMessage="The offer {offerName} has received a new view."
                              values={{
                                offerName: (
                                  <Box component="span" sx={{ color: 'primary.main' }}>
                                    {offerName || '—'}
                                  </Box>
                                )
                              }}
                            />
                          ) : offerTemplate === 'developmentView' ? (
                            <FormattedMessage
                              id="notifications.offer.developmentView.message"
                              defaultMessage="The development {developmentName} in the offer {offerName} has received a new view."
                              values={{
                                offerName: (
                                  <Box component="span" sx={{ color: 'primary.main' }}>
                                    {offerName || '—'}
                                  </Box>
                                ),
                                developmentName: (
                                  <Box component="span" sx={{ color: 'primary.main' }}>
                                    {developmentName || '—'}
                                  </Box>
                                )
                              }}
                            />
                          ) : contactTemplate === 'received' ? (
                            <FormattedMessage
                              id="notifications.contactForm.received.message"
                              defaultMessage="{fullname} has sent a contact form."
                              values={{
                                fullname: (
                                  <Box component="span" sx={{ color: 'primary.main' }}>
                                    {fullname || '—'}
                                  </Box>
                                )
                              }}
                            />
                          ) : developmentEventTemplate === 'showFlat' ? (
                            <FormattedMessage
                              id="notifications.developmentEvent.showFlat.message"
                              defaultMessage="New show flat event ({eventTitle}) in development {developmentName}."
                              values={{
                                eventTitle: (
                                  <Box component="span" sx={{ color: 'primary.main' }}>
                                    {eventTitle || '—'}
                                  </Box>
                                ),
                                developmentName: (
                                  <Box component="span" sx={{ color: 'primary.main' }}>
                                    {developmentName || '—'}
                                  </Box>
                                )
                              }}
                            />
                          ) : developmentEventTemplate === 'openHouse' ? (
                            <FormattedMessage
                              id="notifications.developmentEvent.openHouse.message"
                              defaultMessage="New open house event ({eventTitle}) in development {developmentName}."
                              values={{
                                eventTitle: (
                                  <Box component="span" sx={{ color: 'primary.main' }}>
                                    {eventTitle || '—'}
                                  </Box>
                                ),
                                developmentName: (
                                  <Box component="span" sx={{ color: 'primary.main' }}>
                                    {developmentName || '—'}
                                  </Box>
                                )
                              }}
                            />
                          ) : developmentTemplate === 'sold' ? (
                            <FormattedMessage
                              id="notifications.development.sold.message"
                              defaultMessage="The development {developmentName} has been fully sold."
                              values={{
                                developmentName: (
                                  <Box component="span" sx={{ color: 'primary.main' }}>
                                    {developmentName || '—'}
                                  </Box>
                                )
                              }}
                            />
                          ) : developmentTemplate === 'newStage' ? (
                            <FormattedMessage
                              id="notifications.development.newStage.message"
                              defaultMessage="A new stage ({stageName}) has been added to development {developmentName}."
                              values={{
                                stageName: (
                                  <Box component="span" sx={{ color: 'primary.main' }}>
                                    {stageName || '—'}
                                  </Box>
                                ),
                                developmentName: (
                                  <Box component="span" sx={{ color: 'primary.main' }}>
                                    {developmentName || '—'}
                                  </Box>
                                )
                              }}
                            />
                          ) : developmentTemplate === 'created' ? (
                            <FormattedMessage
                              id="notifications.development.created.message"
                              defaultMessage="The development {developmentName} has been added."
                              values={{
                                developmentName: (
                                  <Box component="span" sx={{ color: 'primary.main' }}>
                                    {developmentName || '—'}
                                  </Box>
                                )
                              }}
                            />
                          ) : (
                            desc || formatRelativeDate(created, intl)
                          );
                        const isUnreadRow = tab === 'unread';
                        const isMockRow = n.notificationid < 0;
                        const target = resolveNotificationTarget(n);
                        const hasTarget = !!target.route || !!target.url;

                        return (
                          <Box
                            key={n.notificationid}
                            component={motion.div}
                            initial={animateItems ? { opacity: 0, y: 8, scale: 0.985 } : false}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={animateItems ? { opacity: 0, y: -6, scale: 0.985 } : undefined}
                            transition={{ duration: 0.34, ease: [0.16, 1, 0.3, 1] }}
                            style={{ willChange: 'transform, opacity' }}
                          >
                            <ListItemButton>
                              <Stack sx={{ width: '100%' }} spacing={0.75}>
                                <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                                  <ListItemAvatar sx={{ minWidth: 40, mt: 0.75, alignSelf: 'flex-start' }}>
                                    <Avatar type={isUnreadRow ? 'filled' : 'outlined'}>{pickIcon(n.type)}</Avatar>
                                  </ListItemAvatar>

                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <ListItemText
                                      primary={
                                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {title}
                                          {isUnreadRow && (
                                            <Box
                                              component="span"
                                              sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                bgcolor: 'primary.main',
                                                display: 'inline-block'
                                              }}
                                            />
                                          )}
                                        </Typography>
                                      }
                                      secondary={
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                          {secondary}
                                        </Typography>
                                      }
                                    />
                                  </Box>

                                  <Typography variant="caption" noWrap sx={{ mt: 0.5, whiteSpace: 'nowrap' }}>
                                    {formatRelativeDate(created, intl)}
                                  </Typography>
                                </Stack>

                                {isUnreadRow && (
                                  <Box
                                    sx={{
                                      width: '100%',
                                      pl: '50px',
                                      display: 'flex',
                                      justifyContent: hasTarget ? 'space-between' : 'flex-end'
                                    }}
                                  >
                                    {hasTarget ? (
                                      <Button
                                        size="small"
                                        variant="text"
                                        sx={{ p: 0, minWidth: 0, textTransform: 'none', fontSize: 12 }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openNotificationTarget(n);
                                        }}
                                      >
                                        <FormattedMessage id="notifications.actions.view" defaultMessage="View" />
                                      </Button>
                                    ) : (
                                      <Box />
                                    )}
                                    <Button
                                      size="small"
                                      variant="text"
                                      disabled={isMockRow}
                                      sx={{ p: 0, minWidth: 0, textTransform: 'none', fontSize: 12 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (isMockRow) return;
                                        markReadAndMove(n);
                                      }}
                                    >
                                      <FormattedMessage id="notifications.actions.markRead" defaultMessage="Mark as read" />
                                    </Button>
                                  </Box>
                                )}
                                {!isUnreadRow && hasTarget && (
                                  <Box sx={{ width: '100%', pl: '50px', display: 'flex', justifyContent: 'flex-start' }}>
                                    <Button
                                      size="small"
                                      variant="text"
                                      sx={{ p: 0, minWidth: 0, textTransform: 'none', fontSize: 12 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openNotificationTarget(n);
                                      }}
                                    >
                                      <FormattedMessage id="notifications.actions.view" defaultMessage="View" />
                                    </Button>
                                  </Box>
                                )}
                              </Stack>
                            </ListItemButton>
                          </Box>
                        );
                      })}
                    </AnimatePresence>
                  </List>
                </Box>
              )}

              <Stack direction="row" justifyContent="center" sx={{ pt: 2 }}>
                {currentHasMore ? (
                  <Button variant="text" disabled={currentLoading} onClick={() => loadList(tab, 'more')} sx={{ textTransform: 'none' }}>
                    {currentLoading
                      ? intl.formatMessage({ id: 'notifications.actions.loading', defaultMessage: 'Loading...' })
                      : intl.formatMessage({ id: 'notifications.actions.loadMore', defaultMessage: 'Load more' })}
                  </Button>
                ) : null}
              </Stack>
            </Box>
          </MainCard>
        )}
      </Drawer>
    </Box>
  );
};

export default NotificationPage;
